/**
 * Movement Reports Page
 * View and filter asset movement reports
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchMovementReports, setFilters } from '../../store/slices/reportSlice';
import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import FilterSection from '../elements/FilterSection';
import useProjectSelector from '../../hooks/useProjectSelector';

const MovementReportsPage = () => {
  const dispatch = useAppDispatch();
  
  const { movementReports, loading, error, filters } = useAppSelector((state) => state.report);
  const { customerList } = useAppSelector((state) => state.customer);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { userGroups } = useAppSelector((state) => state.auth);
  const { ProjectSelectorComponent } = useProjectSelector();
  
  const isAdmin = userGroups?.includes('admins');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Local filter state
  const [selectedCustomer, setSelectedCustomer] = useState(filters.customerId || '');
  const [startDate, setStartDate] = useState(filters.startDate ? new Date(filters.startDate) : null);
  const [endDate, setEndDate] = useState(filters.endDate ? new Date(filters.endDate) : null);

  // Load initial data
  const loadReports = useCallback(() => {
    const customerId = isAdmin ? selectedCustomer : activeCustomer?.id;
    
    dispatch(fetchMovementReports({
      customerId: customerId || undefined,
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    }));
  }, [dispatch, selectedCustomer, startDate, endDate, isAdmin, activeCustomer?.id]);

  useEffect(() => {
    loadReports();
  }, []);

  const handleApplyFilters = () => {
    dispatch(setFilters({
      customerId: selectedCustomer || null,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    }));
    loadReports();
  };

  const handleExport = () => {
    // Simple CSV export
    const headers = ['Asset ID', 'Asset Name', 'From Project', 'To Project', 'Distance', 'Duration', 'Date'];
    const rows = movementReports.map((report) => [
      report.assetId,
      report.assetName || '-',
      report.fromProject || '-',
      report.toProject || '-',
      report.distance ? `${report.distance.toFixed(2)} km` : '-',
      report.duration ? `${Math.round(report.duration / 60)} min` : '-',
      report.timestamp ? new Date(report.timestamp).toISOString() : '-',
    ]);
    
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `movement-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Movement Reports
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadReports}
              disabled={loading}
              sx={{ height: 44 }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || movementReports.length === 0}
              sx={{ height: 44 }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Filters - horizontal alignment, no border, no Filter header/section */}
        <FilterSection>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {isAdmin && (
                <FormControl size="small" sx={{ minWidth: 200, marginBottom: 0 }}>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={selectedCustomer}
                    label="Customer"
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Customers</em>
                    </MenuItem>
                    {(customerList || []).map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <DatePicker
                label="From"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160, marginBottom: 0 } } }}
              />
              
              <DatePicker
                label="To"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160, marginBottom: 0 } } }}
              />
              
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{ height: 40 }}
              >
                Apply
              </Button>
            </Box>
          </LocalizationProvider>
        </FilterSection>

        {/* Status */}
        <Box sx={{ mb: 2 }}>
          <Typography color="text.secondary">
            {loading ? 'Loading...' : `${movementReports.length} records found`}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Asset</TableCell>
                <TableCell>From Project</TableCell>
                <TableCell>To Project</TableCell>
                <TableCell>Distance</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : movementReports.length > 0 ? (
                movementReports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((report, index) => (
                  <TableRow key={report.id || index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {report.assetName || report.assetId}
                      </Typography>
                      {report.assetUuid && (
                        <Typography variant="caption" color="text.secondary">
                          {report.assetUuid}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {report.fromProject || (
                        <Chip label="Unassigned" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {report.toProject || (
                        <Chip label="Unassigned" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {report.distance ? (
                        <Typography variant="body2">
                          {report.distance.toFixed(2)} km
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {report.duration ? (
                        <Typography variant="body2">
                          {Math.round(report.duration / 60)} min
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {report.timestamp ? (
                        <>
                          <Typography variant="body2">
                            {new Date(report.timestamp).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(report.timestamp).toLocaleTimeString()}
                          </Typography>
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No movement reports found. Adjust your filters or check back later.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {movementReports.length > 0 && (
            <TablePagination
              component="div"
              count={movementReports.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </TableContainer>
      </PageBody>
    </Box>
  );
};

export default MovementReportsPage;
